import * as THREE from 'three';
import * as TSL from 'three/tsl';
import { MeshPhysicalNodeMaterial } from 'three/webgpu';

import { EngineClass } from '../../../systems/ClassRegistry.js';
import { property } from '../../../utils/serialization/decorator.js';

import { NodeMaterialAsset } from './NodeMaterialAsset.js';

import type { IDumper, ILoader } from '../../../utils/serialization/factories.js';

/** Thin-film iridescence via `MeshPhysicalNodeMaterial` thickness and iridescence controls. */
@EngineClass('IridescenceNodeMaterialAsset', {
  isNodeMaterialAsset: true,
  nodeMaterialDisplayName: 'Iridescence (Physical)',
  nodeMaterialGroup: 'TSL Node FX',
})
export class IridescenceNodeMaterialAsset extends NodeMaterialAsset(MeshPhysicalNodeMaterial) {

  @property({ type: 'color', description: 'Diffuse color' })
  override color = new THREE.Color(0.9, 0.9, 0.9);

  @property({ type: 'number', min: 0.0, max: 1.0, step: 0.01, description: 'Roughness' })
  override roughness = 0.05;

  @property({ type: 'number', min: 0.0, max: 1.0, step: 0.01, description: 'Metalness' })
  override metalness = 1.0;

  @property({ type: 'number', min: 1.0, max: 2.5, step: 0.01, description: 'Iridescence IOR' })
  override iridescenceIOR = 1.8;

  @property({ type: 'number', min: 100, max: 1200, step: 10, description: 'Iridescence thickness (nm)' })
    iridescenceThickness = 400;

  constructor() {
    super();
    this.rebuild();
  }

  override rebuild(): void {
    this.applyCommonMaterialState();
    this.roughnessNode = TSL.float(this.roughness);
    this.metalnessNode = TSL.float(this.metalness);
    this.colorNode = null;
    (this as any).iridescenceNode = TSL.float(1.0);
    (this as any).iridescenceIORNode = TSL.float(this.iridescenceIOR);
    (this as any).iridescenceThicknessNode = TSL.float(this.iridescenceThickness);
  }

  public serialize(dumper: IDumper): void {
    this.serializeAuthoredFields(dumper);
  }

  public static staticDeserialize(data: unknown, loader: ILoader): IridescenceNodeMaterialAsset {
    const instance = new IridescenceNodeMaterialAsset();
    IridescenceNodeMaterialAsset.loadAuthoredFields(instance, loader);
    instance.rebuild();
    return instance;
  }
}
